import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  CircularProgress,
  IconButton,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";
import { ChevronDown, Download } from "lucide-react";

export const SectionTitle: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const theme = useTheme();
  return (
    <Typography
      sx={{
        fontSize: "20px",
        fontWeight: 700,
        color: theme.palette.primary.main,
      }}
    >
      {children}
    </Typography>
  );
};

export const SectionHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
  sx?: Record<string, any>;
}> = ({ title, subtitle, action, sx }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 2,
        ...sx,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <SectionTitle>{title}</SectionTitle>
        {subtitle && (
          <Typography
            sx={{
              fontSize: "13px",
              color: theme.palette.text.primary,
              mt: 0.5,
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
  );
};

export const DetailAccordion: React.FC<{
  title: React.ReactNode;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  sxAccordion?: SxProps;
}> = ({ title, defaultExpanded = false, children, sxAccordion }) => {
  const theme = useTheme();
  return (
    <Accordion
      defaultExpanded={defaultExpanded}
      disableGutters
      elevation={0}
      sx={{
        backgroundColor: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px !important",
        "&:before": { display: "none" },
        ...sxAccordion,
      }}
    >
      <AccordionSummary
        expandIcon={
          <ChevronDown size={20} color={theme.palette.text.primary} />
        }
        sx={{ px: 2, "& .MuiAccordionSummary-content": { my: 1.5 } }}
      >
        <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>
          {title}
        </Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ px: 2, py: 2 }}>{children}</AccordionDetails>
    </Accordion>
  );
};

export interface InfoItem {
  label: string;
  value?: React.ReactNode;
}

export const hasValue = (v: InfoItem["value"]) =>
  v !== undefined && v !== null && v !== "";

export const InfoGrid: React.FC<{ items: InfoItem[] }> = ({ items }) => {
  const theme = useTheme();
  const visible = items.filter((i) => hasValue(i.value));
  if (visible.length === 0) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
        gap: 2.5,
      }}
    >
      {visible.map((item, index) => (
        <Box key={`${item.label}-${index}`}>
          <Typography
            sx={{
              fontSize: "0.75rem",
              fontWeight: 600,
              color: theme.palette.text.primary,
              ml: "0.75rem",
            }}
          >
            {item.label}
          </Typography>
          <Box
            sx={{
              backgroundColor: theme.palette.action.hover,
              // border: `1px solid ${theme.palette.divider}`,
              borderRadius: "8px",
              padding: "12px 16px",
            }}
          >
            <Typography sx={{ fontSize: "1rem", overflowWrap: "anywhere" }}>
              {item.value}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export const FieldGroup: React.FC<{ title: string; items: InfoItem[] }> = ({
  title,
  items,
}) => {
  const visible = items.filter((i) => hasValue(i.value));
  if (visible.length === 0) return null;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography sx={{ fontSize: "1.25rem", fontWeight: 700 }}>
        {title}
      </Typography>
      <InfoGrid items={visible} />
    </Box>
  );
};

export const DownloadCard: React.FC<{
  title: string;
  id?: string | number;
  loading?: boolean;
  onDownload?: () => void;
}> = ({ title, id, loading, onDownload }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 1.5,
        height: "55px",
        backgroundColor: theme.palette.action.hover,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: "8px",
        padding: "16px",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          minWidth: 0,
          flex: 1,
        }}
      >
        <Typography
          sx={{
            fontSize: "14px",
            fontWeight: 700,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {title}
        </Typography>
        {id !== undefined && id !== "" && (
          <>
            <Box
              sx={{
                width: "1px",
                height: "16px",
                backgroundColor: theme.palette.divider,
                flexShrink: 0,
              }}
            />
            <Typography
              sx={{
                fontSize: "14px",
                color: theme.palette.text.primary,
                whiteSpace: "nowrap",
              }}
            >
              ID: {id}
            </Typography>
          </>
        )}
      </Box>
      <IconButton
        onClick={onDownload}
        disabled={loading}
        aria-label="download"
        sx={{ color: theme.palette.primary.main, flexShrink: 0 }}
      >
        {loading ? (
          <CircularProgress size={20} color="inherit" />
        ) : (
          <Download size={20} />
        )}
      </IconButton>
    </Box>
  );
};

export const downloadGridSx = {
  display: "grid",
  gridTemplateColumns: {
    xs: "1fr",
    sm: "repeat(2, 1fr)",
    xl: "repeat(3, 1fr)",
  },
  gap: 2,
};

export const searchBoxSx = {
  flex: 1,
  minWidth: 220,
  "& .MuiFormControl-root": {
    minWidth: "0 !important",
    width: "100% !important",
  },
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};

export const selectBoxSx = {
  width: { xs: "100%", sm: 180 },
  "& > *": { minWidth: "0 !important", width: "100% !important" },
  "& .MuiFormControl-root": {
    minWidth: "0 !important",
    width: "100% !important",
  },
  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
};
